import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { createOssOrder, updateOssOrder, uploadOssDocument } from "../api/ossApi";

const today = () => new Date().toISOString().slice(0, 10);

const emptyLine = () => ({
  itemCode: "",
  itemName: "",
  specification: "",
  quantity: 1,
  unitCode: "EA",
  requestedDueDate: today(),
  standardLeadDays: 10,
  planningBufferDays: 5,
  dayBasis: "BUSINESS",
  productionPlanStatus: "NOT_REVIEWED",
});

const makeOrderNo = () => {
  const now = new Date();
  const ymd = now.toISOString().slice(0, 10).replaceAll("-", "");
  const time = [
    now.getHours(),
    now.getMinutes(),
    now.getSeconds(),
  ].map((value) => String(value).padStart(2, "0")).join("");
  const suffix = Math.random().toString(36).slice(2, 5).toUpperCase();
  return `ORD-${ymd}-${time}-${suffix}`;
};

const emptyForm = () => ({
  orderNo: makeOrderNo(),
  customerName: "",
  siteName: "",
  customerPoNo: "",
  orderDate: today(),
  orderStatus: "RECEIVED",
  note: "",
  lines: [emptyLine()],
});

const documentTypes = [
  ["PURCHASE_ORDER", "발주서"],
  ["ORDER_FORM", "주문서"],
  ["CHANGE_ORDER", "변경 근거"],
  ["SPEC", "규격서"],
  ["OTHER", "기타"],
];

const fileAccept = ".pdf,image/*,.jpg,.jpeg,.png,.webp,.bmp,.gif,.xlsx,.xls,.doc,.docx";

const emptyDocumentSlot = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  documentType: "PURCHASE_ORDER",
  file: null,
});

const normalizeDetailToForm = (detail) => {
  if (!detail?.order) return emptyForm();
  return {
    orderNo: detail.order.order_no || "",
    customerName: detail.order.customer_name || "",
    siteName: detail.order.site_name === "-" ? "" : detail.order.site_name || "",
    customerPoNo: detail.order.customer_po_no || "",
    orderDate: detail.order.order_date || today(),
    orderStatus: detail.order.order_status || "RECEIVED",
    note: detail.order.note || "",
    lines: (detail.lines || []).map((line, index) => ({
      orderLineId: line.order_line_id,
      lineNo: line.line_no || index + 1,
      itemCode: line.item_code_snapshot || "",
      itemName: line.item_name_snapshot || "",
      specification: line.specification_snapshot || "",
      quantity: Number(line.order_quantity || 1),
      unitCode: line.unit_code || "EA",
      requestedDueDate: line.requested_due_date || today(),
      standardLeadDays: Number(line.standard_lead_days || 0),
      planningBufferDays: Number(line.planning_buffer_days || 0),
      dayBasis: line.day_basis || "BUSINESS",
      productionPlanStatus: line.production_plan_status || "NOT_REVIEWED",
    })),
  };
};

export const OssOrderFormDialog = ({
  open,
  onClose,
  groupCode,
  projectCode,
  orderId,
  initialData,
  onSaved,
  onCreated,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [documentSlots, setDocumentSlots] = useState(() => [emptyDocumentSlot()]);

  const isEdit = Boolean(orderId);
  const title = useMemo(() => (open ? (isEdit ? "주문 수정" : "주문 등록") : ""), [open, isEdit]);

  useEffect(() => {
    if (!open) return;
    setError("");
    setForm(isEdit ? normalizeDetailToForm(initialData) : emptyForm());
    setDocumentSlots([emptyDocumentSlot()]);
  }, [open, isEdit, initialData]);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const updateLine = (index, key, value) =>
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.map((line, lineIndex) =>
        lineIndex === index ? { ...line, [key]: value } : line
      ),
    }));

  const addLine = () => setForm((prev) => ({ ...prev, lines: [...prev.lines, emptyLine()] }));
  const removeLine = (index) =>
    setForm((prev) => ({
      ...prev,
      lines:
        prev.lines.length === 1 || prev.lines[index]?.orderLineId
          ? prev.lines
          : prev.lines.filter((_, lineIndex) => lineIndex !== index),
    }));
  const addDocumentSlot = () => setDocumentSlots((prev) => [...prev, emptyDocumentSlot()]);
  const updateDocumentSlot = (slotId, key, value) =>
    setDocumentSlots((prev) =>
      prev.map((slot) => (slot.id === slotId ? { ...slot, [key]: value } : slot))
    );
  const removeDocumentSlot = (slotId) =>
    setDocumentSlots((prev) =>
      prev.length === 1
        ? [{ ...prev[0], file: null, documentType: "PURCHASE_ORDER" }]
        : prev.filter((slot) => slot.id !== slotId)
    );

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const saved = isEdit
        ? await updateOssOrder(groupCode, projectCode, orderId, form)
        : await createOssOrder(groupCode, projectCode, form);
      const uploadSlots = documentSlots.filter((slot) => slot.file);
      if (!isEdit && uploadSlots.length > 0) {
        await Promise.all(
          uploadSlots.map((slot) =>
            uploadOssDocument(groupCode, projectCode, slot.file, {
              orderId: saved.orderId,
              documentType: slot.documentType,
              documentSlotKey: slot.id,
              userKey: "document.manager",
            })
          )
        );
      }
      onCreated?.(saved);
      onSaved?.(saved);
      onClose?.();
      setForm(emptyForm());
      setDocumentSlots([emptyDocumentSlot()]);
    } catch (e) {
      setError(e.response?.data?.message || `주문 ${isEdit ? "수정" : "등록"} 중 오류가 발생했습니다.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          overflow: "hidden",
          maxHeight: "calc(100dvh - 48px)",
        },
      }}
    >
      <DialogTitle
        sx={{
          minHeight: 62,
          px: "24px !important",
          py: "18px !important",
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #e5e7eb",
          fontSize: "20px !important",
          fontWeight: "800 !important",
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent
        sx={{
          p: 2.5,
          bgcolor: "#f8fafc",
          overflow: "auto",
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb": { borderRadius: 999, backgroundColor: "#cbd5e1" },
          "&::-webkit-scrollbar-track": { backgroundColor: "#f8fafc" },
        }}
      >
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          <Box
            sx={{
              p: 2.15,
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              bgcolor: "#fff",
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
              gap: 1.45,
            }}
          >
            <Box sx={{ gridColumn: { xs: "auto", md: "1 / -1" }, mb: 0.25 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>주문 기본 정보</Typography>
              <Typography sx={{ mt: 0.25, fontSize: 12.5, color: "#64748b" }}>
                주문번호는 자동으로 채워지며, 저장 시 서버에서 중복 여부를 다시 확인합니다.
              </Typography>
            </Box>
            <TextField
              label="주문번호"
              value={form.orderNo}
              onChange={(e) => update("orderNo", e.target.value)}
              required
              disabled={isEdit}
              helperText={isEdit ? "주문번호는 등록 후 변경하지 않습니다." : "자동으로 채워지며, 필요하면 등록 전에 수정할 수 있습니다."}
            />
            <TextField label="거래처" value={form.customerName} onChange={(e) => update("customerName", e.target.value)} required />
            <TextField label="납품처/현장" value={form.siteName} onChange={(e) => update("siteName", e.target.value)} />
            <TextField label="고객 발주번호" value={form.customerPoNo} onChange={(e) => update("customerPoNo", e.target.value)} />
            <TextField type="date" label="주문일자" value={form.orderDate} onChange={(e) => update("orderDate", e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField select label="주문 상태" value={form.orderStatus} onChange={(e) => update("orderStatus", e.target.value)}>
              <MenuItem value="RECEIVED">주문 접수</MenuItem>
              <MenuItem value="CONFIRMED">주문 확정</MenuItem>
              <MenuItem value="HOLD">보류</MenuItem>
              <MenuItem value="DRAFT">임시 저장</MenuItem>
            </TextField>
            <TextField
              label="비고"
              value={form.note}
              onChange={(e) => update("note", e.target.value)}
              multiline
              minRows={2}
              sx={{ gridColumn: { xs: "auto", md: "1 / span 3" } }}
            />
          </Box>

          <Box sx={{ p: 2.15, border: "1px solid #e5e7eb", borderRadius: "8px", bgcolor: "#fff" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>주문 품목</Typography>
              <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={addLine}>
                품목 추가
              </Button>
            </Stack>

            <Stack spacing={1.2}>
              {form.lines.map((line, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 1.45,
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "120px minmax(180px, 1.2fr) minmax(160px, 1fr) 100px 80px 145px 130px 130px 44px",
                    },
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  <TextField label="품목코드" value={line.itemCode} onChange={(e) => updateLine(index, "itemCode", e.target.value)} size="small" />
                  <TextField label="품목명" value={line.itemName} onChange={(e) => updateLine(index, "itemName", e.target.value)} size="small" required />
                  <TextField label="규격" value={line.specification} onChange={(e) => updateLine(index, "specification", e.target.value)} size="small" />
                  <TextField type="number" label="수량" value={line.quantity} onChange={(e) => updateLine(index, "quantity", e.target.value)} size="small" />
                  <TextField label="단위" value={line.unitCode} onChange={(e) => updateLine(index, "unitCode", e.target.value)} size="small" />
                  <TextField type="date" label="요청 납기" value={line.requestedDueDate} onChange={(e) => updateLine(index, "requestedDueDate", e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
                  <TextField type="number" label="소요일" value={line.standardLeadDays} onChange={(e) => updateLine(index, "standardLeadDays", e.target.value)} size="small" />
                  <TextField type="number" label="여유일" value={line.planningBufferDays} onChange={(e) => updateLine(index, "planningBufferDays", e.target.value)} size="small" />
                  <IconButton
                    onClick={() => removeLine(index)}
                    disabled={form.lines.length === 1 || Boolean(line.orderLineId)}
                    title={line.orderLineId ? "등록된 품목 삭제는 주문 취소/삭제 흐름에서 처리합니다." : "품목 제거"}
                  >
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          </Box>

          {!isEdit && (
            <Box sx={{ p: 2.15, border: "1px solid #e5e7eb", borderRadius: "8px", bgcolor: "#fff" }}>
              <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} justifyContent="space-between" spacing={1.2} sx={{ mb: 1.4 }}>
                <Box>
                  <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>첨부 문서</Typography>
                  <Typography sx={{ mt: 0.25, fontSize: 12.5, color: "#64748b" }}>
                    유형과 파일을 한 줄씩 지정해서 주문 접수 근거를 함께 등록할 수 있습니다.
                  </Typography>
                </Box>
                <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={addDocumentSlot}>
                  문서 추가
                </Button>
              </Stack>

              <Stack spacing={1}>
                {documentSlots.map((slot, index) => (
                  <Box
                    key={slot.id}
                    sx={{
                      p: 1.35,
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      bgcolor: "#f8fafc",
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "160px minmax(220px, 1fr) 44px" },
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    <TextField
                      select
                      size="small"
                      label={`문서 유형 ${index + 1}`}
                      value={slot.documentType}
                      onChange={(e) => updateDocumentSlot(slot.id, "documentType", e.target.value)}
                    >
                      {documentTypes.map(([value, label]) => (
                        <MenuItem key={value} value={value}>{label}</MenuItem>
                      ))}
                    </TextField>
                    <Button
                      component="label"
                      variant={slot.file ? "contained" : "outlined"}
                      startIcon={<UploadFileRoundedIcon />}
                      sx={{
                        justifyContent: "flex-start",
                        minWidth: 0,
                        textTransform: "none",
                        bgcolor: slot.file ? "#6c5ce7" : undefined,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {slot.file ? slot.file.name : "파일 선택"}
                      </Box>
                      <input
                        hidden
                        type="file"
                        accept={fileAccept}
                        onClick={(event) => {
                          event.currentTarget.value = "";
                        }}
                        onChange={(event) =>
                          updateDocumentSlot(slot.id, "file", event.target.files?.[0] || null)
                        }
                      />
                    </Button>
                    <IconButton
                      onClick={() => removeDocumentSlot(slot.id)}
                      title={documentSlots.length === 1 ? "선택 파일 비우기" : "첨부 문서 슬롯 삭제"}
                    >
                      <DeleteOutlineRoundedIcon />
                    </IconButton>
                  </Box>
                ))}
              </Stack>

              <Typography sx={{ mt: 1, fontSize: 12.5, color: "#64748b" }}>
                주문 등록 시 첨부한 문서는 주문 단위 문서로 저장됩니다. 품목별 문서는 상세관리의 문서 탭에서 추가할 수 있습니다.
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.6, borderTop: "1px solid #e5e7eb", bgcolor: "#fff" }}>
        <Button onClick={onClose} disabled={submitting} color="inherit" sx={{ minWidth: 78, height: 36 }}>취소</Button>
        <Button onClick={submit} disabled={submitting} variant="contained" sx={{ minWidth: 104, height: 36 }}>
          {isEdit ? "수정 완료" : "등록 완료"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
